import type { FleetQuestionRequest } from "./controllers/fleet-question.ts";
import type { FleetQuestionLastAnswerEntry } from "./controllers/fleet-question.ts";
import type { FleetTodoItem } from "./controllers/fleet-todos.ts";
import type { GatewayBrowserClient } from "./gateway.ts";
import { loadFleetInteractionForSession } from "./controllers/fleet-interaction-sync.ts";
import {
  buildFleetQuestionAnswers,
  pendingFleetQuestionsForSession,
  pruneFleetQuestionQueue,
  removeFleetQuestion,
} from "./controllers/fleet-question.ts";

export type FleetInteractionHost = {
  sessionKey: string;
  client: GatewayBrowserClient | null;
  /**
   * JAISIU-types-sweep: optional on the type — older host objects
   * (and the smaller test fixtures) don't carry the queue state and
   * rely on the controller to fall back to a fresh snapshot. The
   * production host always populates these fields; optional lets
   * unit tests synthesize a minimal host without redefining every
   * key.
   */
  fleetQuestionQueue?: FleetQuestionRequest[];
  fleetQuestionBusy?: boolean;
  fleetQuestionError?: string | null;
  fleetQuestionDraft?: Record<string, string[]>;
  fleetQuestionFreeText?: Record<string, string>;
  fleetTodosBySession?: Record<string, FleetTodoItem[]>;
  /**
   * Compact echo of the last submitted Ask answer (cleared when the
   * assistant continues). Optional — older test fixtures and the
   * production host both set this freely; the controller coalesces
   * missing values to `null`.
   */
  fleetQuestionLastAnswer?:
    | import("./controllers/fleet-question.ts").FleetQuestionLastAnswer
    | null;
  requestUpdate?: () => unknown;
  /** Optional callback: refresh the chat transcript so the assistant's
   *  reply (printed after the question is answered) appears without a
   *  manual reload. */
  refreshChatHistory?: (opts?: { quiet?: boolean }) => Promise<void> | void;
};

export async function refreshFleetInteraction(app: FleetInteractionHost): Promise<void> {
  const sessionKey = app.sessionKey.trim();
  if (!app.client || !sessionKey) {
    return;
  }
  try {
    const { questions, todos } = await loadFleetInteractionForSession({
      client: app.client,
      sessionKey,
    });
    // JAISIU-types-sweep: coalesce the optional queue state via `??`
    // on read; the assignment is unconditional on write because the
    // controller owns the host shape and pins the result.
    const queue = app.fleetQuestionQueue ?? [];
    const todosBySession = app.fleetTodosBySession ?? {};
    const otherSessions = queue.filter((q) => q.sessionKey !== sessionKey);
    app.fleetQuestionQueue = [...otherSessions, ...questions];
    app.fleetTodosBySession = { ...todosBySession, [sessionKey]: todos };
  } catch {
    /* best-effort hydrate */
  }
}

export function activeFleetQuestionForSession(app: FleetInteractionHost) {
  return pendingFleetQuestionsForSession(
    app.fleetQuestionQueue ?? [],
    app.sessionKey,
  )[0] ?? null;
}

/** First pending question across all sessions — for the global overlay (any tab). */
export function activeFleetQuestionGlobal(app: FleetInteractionHost) {
  return pruneFleetQuestionQueue(app.fleetQuestionQueue ?? []).find(
    (entry) => !entry.answered,
  ) ?? null;
}

export function fleetTodosForActiveSession(app: FleetInteractionHost): FleetTodoItem[] {
  const key = app.sessionKey.trim();
  if (!key) {
    return [];
  }
  return (app.fleetTodosBySession ?? {})[key] ?? [];
}

export function resetFleetQuestionDraft(app: FleetInteractionHost): void {
  app.fleetQuestionDraft = {};
  app.fleetQuestionFreeText = {};
}

export function handleFleetQuestionToggleOption(
  app: FleetInteractionHost,
  questionId: string,
  optionId: string,
  allowMultiple: boolean,
): void {
  const drafts = app.fleetQuestionDraft ?? {};
  const current = drafts[questionId] ?? [];
  if (allowMultiple) {
    const next = current.includes(optionId)
      ? current.filter((id) => id !== optionId)
      : [...current, optionId];
    app.fleetQuestionDraft = { ...drafts, [questionId]: next };
  } else {
    app.fleetQuestionDraft = { ...drafts, [questionId]: [optionId] };
  }
}

export function handleFleetQuestionFreeTextChange(
  app: FleetInteractionHost,
  questionId: string,
  value: string,
): void {
  app.fleetQuestionFreeText = {
    ...(app.fleetQuestionFreeText ?? {}),
    [questionId]: value,
  };
}

export async function handleFleetQuestionSubmit(app: FleetInteractionHost): Promise<void> {
  // Prefer session-matched ask when on that chat; otherwise answer the global head of queue.
  const active = activeFleetQuestionForSession(app) ?? activeFleetQuestionGlobal(app);
  if (!active || !app.client || app.fleetQuestionBusy) {
    return;
  }
  app.fleetQuestionBusy = true;
  app.fleetQuestionError = null;
  try {
    const answers = buildFleetQuestionAnswers(
      active,
      app.fleetQuestionDraft ?? {},
      app.fleetQuestionFreeText ?? {},
    );
    // Capture a human-readable echo of the answer before sending, so the
    // operator can confirm what was selected (and what reached the
    // gateway) by seeing it as a chat bubble below the question.
    const labelById = new Map<string, string>();
    for (const q of active.questions) {
      for (const opt of q.options) {
        labelById.set(opt.id, opt.label);
      }
    }
    app.fleetQuestionLastAnswer = {
      requestTitle: active.title,
      answers: active.questions.map<FleetQuestionLastAnswerEntry>((q) => {
        const answer = answers.find((a) => a.questionId === q.id);
        const selectedOptionIds = answer?.selectedOptionIds ?? [];
        return {
          questionId: q.id,
          questionPrompt: q.prompt,
          // Keep options/allowMultiple populated for backwards-safe
          // echo payloads (compact view only needs labels + free-text).
          options: q.options.map((opt) => ({
            id: opt.id,
            label: opt.label,
          })),
          allowMultiple: q.allowMultiple === true,
          selectedOptionIds,
          selectedOptionLabels: selectedOptionIds.map((id) => labelById.get(id) ?? id),
          freeText: answer?.freeText ?? null,
        };
      }),
      submittedAtMs: Date.now(),
    };
    // Collapse immediately: drop the interactive card from the queue so
    // the compact "You answered" echo is the only Ask UI left. The
    // gateway `fleet.question.resolved` event also removes by id (no-op).
    // JAISIU-types-sweep: `fleetQuestionQueue` is optional on the
    // host (unit-test fixtures omit it) and `removeFleetQuestion`
    // returns a plain array, so the `?? []` fallback keeps the write
    // well-typed. Same result as before when the queue IS populated —
    // `active` was resolved out of it a few lines above.
    app.fleetQuestionQueue = removeFleetQuestion(app.fleetQuestionQueue ?? [], active.id);
    resetFleetQuestionDraft(app);
    // Force a Lit update so the chat view swaps questionnaire → echo
    // without waiting for the next poll tick.
    if (typeof app.requestUpdate === "function") {
      void app.requestUpdate();
    }
    // Fire-and-await the gateway resolve. If it fails (e.g. unknown id
    // because we injected a synthetic question for local testing),
    // surface as a non-fatal error.
    app.client.request("fleet.question.resolve", { id: active.id, answers }).catch((err) => {
      app.fleetQuestionError = `Gateway rejected the answer: ${String(err)}`;
    });
  } catch (err) {
    app.fleetQuestionError = `Could not submit answer: ${String(err)}`;
  } finally {
    app.fleetQuestionBusy = false;
  }
}

/**
 * Apply the "answered" visual state to the inline fleet-question bubble
 * directly in the DOM. Lit's template cache can fail to re-evaluate
 * bindings on the row template instance, so this gives the operator
 * immediate feedback (gray-out + Continue replaced + "Answered"
 * subtitle) without waiting for a full Lit re-render.
 *
 * Safe to call multiple times — the DOM operations are idempotent.
 */
export function applyAnsweredVisualsToFleetQuestionBubble(app: FleetInteractionHost): void {
  if (typeof document === "undefined") {
    return;
  }
  const root = app as unknown as { renderRoot?: Document | ShadowRoot };
  const scope = root.renderRoot ?? document;
  const card = scope.querySelector(".chat-group--fleet-question");
  if (!card) {
    return;
  }
  // Mark the bubble as answered.
  card.setAttribute("data-answered", "true");
  // Disable every interactive control inside the bubble.
  card
    .querySelectorAll<HTMLElement>('button, input, textarea, select, [role="button"], fieldset')
    .forEach((el) => {
      el.setAttribute("disabled", "disabled");
      el.setAttribute("aria-disabled", "true");
    });
  // Replace the Continue button with an "Answered" label so the
  // operator gets confirmation even if Lit's update lands later.
  const continueBtn = Array.from(card.querySelectorAll("button")).find((b) =>
    (b.textContent ?? "").trim().toLowerCase().startsWith("continue"),
  );
  if (continueBtn && !continueBtn.hasAttribute("data-replaced")) {
    continueBtn.setAttribute("data-replaced", "true");
    const replacement = document.createElement("span");
    replacement.setAttribute("data-testid", "fleet-question-answered-badge");
    replacement.className = "fleet-question-card__answered-badge";
    replacement.textContent = "✓ Answered";
    continueBtn.replaceWith(replacement);
  }
  // Update the subtitle from "expires in 9m" to "Answered".
  const sub = card.querySelector(".fleet-question-card__sub");
  if (sub && !sub.textContent?.includes("Answered")) {
    sub.textContent = "Answered · chat below to keep going";
  }
  // Dim the title.
  const title = card.querySelector(".fleet-question-card__title");
  if (title) {
    (title as HTMLElement).style.textDecoration = "line-through";
    (title as HTMLElement).style.textDecorationColor = "rgba(180, 210, 235, 0.5)";
    (title as HTMLElement).style.textDecorationThickness = "1px";
  }
  // Insert the operator's answer summary echo right after the bubble if
  // not already there.
  if (!card.parentElement?.querySelector('[data-testid="fleet-question-answered-badge-inline"]')) {
    const echo = document.createElement("div");
    echo.setAttribute("data-testid", "fleet-question-answered-badge-inline");
    echo.className = "fleet-question-card__answer-summary";
    const last = app.fleetQuestionLastAnswer;
    if (last) {
      echo.innerHTML = renderInlineAnswerSummary(last);
    }
    card.parentElement?.appendChild(echo);
  }
}

function renderInlineAnswerSummary(last: NonNullable<unknown>): string {
  // last is `FleetQuestionLastAnswer` — narrow by duck-typing.
  type AnswerEcho = {
    questionPrompt: string;
    selectedOptionLabels: string[];
    freeText: string | null;
  };
  type LastAnswer = {
    answers: AnswerEcho[];
  };
  const a = last as LastAnswer;
  if (!Array.isArray(a.answers)) return "";
  const items = a.answers
    .map((ans) => {
      const picks = ans.selectedOptionLabels?.length
        ? ans.selectedOptionLabels.join(", ")
        : "(none)";
      const free = ans.freeText ? ` · "${ans.freeText}"` : "";
      return `<div class="fleet-question-card__answer-summary-line">
        <span class="fleet-question-card__answer-summary-prompt">${escapeHtml(ans.questionPrompt ?? "")}</span>
        <span class="fleet-question-card__answer-summary-picks">${escapeHtml(picks)}${escapeHtml(free)}</span>
      </div>`;
    })
    .join("");
  return items;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export function setFleetTodosForSession(
  app: FleetInteractionHost,
  sessionKey: string,
  todos: FleetTodoItem[],
): void {
  const key = sessionKey.trim();
  if (!key) {
    return;
  }
  app.fleetTodosBySession = { ...app.fleetTodosBySession, [key]: todos };
}
